import { Button, Card, Divider, Flex, Modal, Space, Tag, Typography, notification } from 'antd';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCard } from '../context/CardContext';
import { useOnboarding } from '../context/OnboardingContext';
import { useTelegram } from '../context/TelegramContext';
import { buildBotPath } from '../utils/routes';
import { formatCurrency, formatDateTime, maskCardPan } from '../utils/format';
import { SUPPORT_LINK, openTelegramLink } from '../utils/telegramLink';

// ==============================|| CLASSIC SUBSCRIPTION PANEL ||============================== //
//
// The subscription card stack — status, active card, pending-block notice, and the unsubscribe/
// history actions. Rendered inside the vivid dashboard's "Obunani boshqarish" section.

const ClassicSubscriptionPanel = () => {
    const navigate = useNavigate();
    const { bot_id } = useParams();
    const { selectedTariff } = useCard();
    const { subscriptionInfo, subscriptionStatus, history, activeCard, unsubscribe, unsubscribeResult, isUnsubscribing, reloadDashboard } = useOnboarding();
    const { webApp } = useTelegram();
    const [api, contextHolder] = notification.useNotification();

    // A charge that has been started but has not landed yet. Reporting it as "Faol emas" was the
    // reason a subscriber who had just paid was told their subscription did not exist.
    const isPaymentPending = !subscriptionStatus?.is_active && Boolean(subscriptionStatus?.pending_payment);
    const subscriptionStateLabel = subscriptionStatus?.is_active
        ? 'Faol'
        : isPaymentPending
            ? "To'lov ishlanmoqda"
            : 'Faol emas';
    const currentTariff = subscriptionStatus?.tariff || selectedTariff;

    // The subscription has already been cancelled for good; the paid period is just running out.
    // Cancelling again is impossible, so the only action left is contacting support.
    const pendingBlock = subscriptionStatus?.block?.state === 'pending' ? subscriptionStatus.block : null;

    const nextPaymentText = useMemo(() => {
        // The schedule the cron actually reads. Everything below is a fallback for older responses
        // and drifts from the stored `pay_time` once a payment is retried or delayed.
        const nextPayment = subscriptionStatus?.next_payment;
        if (nextPayment) {
            return `${formatDateTime(nextPayment.pay_time)} · ${formatCurrency(nextPayment.amount, nextPayment.currency)}`;
        }

        // A block means the subscription was cancelled, which deleted the schedule — there is no
        // next charge. Every fallback below invents one from `end_time` or from the last paid
        // invoice, so without this the screen kept naming a date and a sum after cancelling.
        if (subscriptionStatus?.block) {
            return 'Bekor qilingan';
        }

        if (subscriptionStatus?.end_time && currentTariff) {
            return `${formatDateTime(subscriptionStatus.end_time)} · ${formatCurrency(currentTariff.amount, currentTariff.currency)}`;
        }

        const now = Date.now();
        const pendingWithPaymentTime = [...history]
            .filter((item) => item.status === 'pending' && item.payment_time)
            .sort((a, b) => new Date(a.payment_time!.replace(' ', 'T')).getTime() - new Date(b.payment_time!.replace(' ', 'T')).getTime());

        const nextPending =
            pendingWithPaymentTime.find((item) => new Date(item.payment_time!.replace(' ', 'T')).getTime() >= now) ||
            pendingWithPaymentTime[0];

        if (nextPending?.payment_time) {
            return `${formatDateTime(nextPending.payment_time)} · ${formatCurrency(nextPending.amount, 'UZS')}`;
        }

        const latestPaid = [...history]
            .filter((item) => item.is_paid)
            .sort((a, b) => new Date(b.created_time.replace(' ', 'T')).getTime() - new Date(a.created_time.replace(' ', 'T')).getTime())[0];

        if (!latestPaid) {
            return '—';
        }

        const tariffForPeriod =
            currentTariff ||
            subscriptionInfo?.tariffs?.find((tariff) => tariff.id === latestPaid.tariff_id);

        if (!tariffForPeriod) {
            return `— · ${formatCurrency(latestPaid.amount, 'UZS')}`;
        }

        const paidAt = new Date(latestPaid.created_time.replace(' ', 'T'));
        if (Number.isNaN(paidAt.getTime())) {
            return `— · ${formatCurrency(tariffForPeriod.amount, tariffForPeriod.currency)}`;
        }

        const nextPaymentAt = new Date(paidAt.getTime() + tariffForPeriod.period_seconds * 1000);

        return `${formatDateTime(nextPaymentAt.toISOString())} · ${formatCurrency(tariffForPeriod.amount, tariffForPeriod.currency)}`;
    }, [currentTariff, history, subscriptionInfo, subscriptionStatus]);

    const handleUnsubscribe = () => {
        Modal.confirm({
            title: 'Obunani bekor qilish',
            centered: true,
            width: 'calc(100vw - 32px)',
            content: (
                <div>
                    <p>
                        Sizning kirish huquqingiz faol davr tugaguncha saqlanadi.
                    </p>
                    <p>
                        Kirish {formatDateTime(subscriptionStatus?.end_time)} gacha davom etadi.
                    </p>
                    <p>
                        Kirish muddati tugagandan so'ng, 6 oy davomida qayta obuna bo'la olmaysiz.
                    </p>
                </div>
            ),
            okText: 'Davom etish',
            cancelText: 'Bekor qilish',
            onOk: () => {
                Modal.confirm({
                    title: 'Siz ishonchingiz komilmi?',
                    centered: true,
                    width: 'calc(100vw - 32px)',
                    content: "Bu amal avtomatik yechib olishni o'chiradi va uni ortga qaytarib bo'lmaydi.",
                    okText: 'Ha, bekor qilinsin',
                    cancelText: 'Yo‘q',
                    onOk: async () => {
                        const result = await unsubscribe();
                        api.success({
                            message: 'Obuna bekor qilindi',
                            description: result.access_until
                                ? `Kirish ${formatDateTime(result.access_until)} gacha saqlanadi.`
                                : 'Kirish faol davr oxirigacha saqlanadi.',
                            placement: 'bottom',
                        });
                        await reloadDashboard();
                    },
                });
            },
        });
    };

    return (
        <>
            {contextHolder}
            <Flex vertical gap={16}>
                <Card className="info-panel" bordered={false}>
                    <Flex justify="space-between" align="center" wrap gap={12}>
                        <div>
                            <Typography.Text className="muted-label">Holat</Typography.Text>
                            {/* Never the raw API enum: an unhandled value used to reach the screen
                                verbatim, which is how a subscriber was shown the word `inactive`. */}
                            <Typography.Title level={4} className="section-title">
                                {subscriptionStateLabel}
                            </Typography.Title>
                        </div>
                        <Tag color={subscriptionStatus?.is_active ? 'success' : isPaymentPending ? 'processing' : 'error'}>
                                {subscriptionStateLabel}
                        </Tag>
                    </Flex>
                    <Divider />
                    {isPaymentPending && (
                        <Typography.Paragraph className="section-text">
                            To'lov tasdiqlanmoqda. Natija bot orqali yuboriladi.
                        </Typography.Paragraph>
                    )}
                    <Typography.Paragraph className="section-text">
                        Keyingi to'lov: {nextPaymentText}
                    </Typography.Paragraph>
                    {currentTariff && (
                        <Typography.Paragraph className="section-text">
                            Tarif: {currentTariff.title}
                        </Typography.Paragraph>
                    )}
                </Card>

                <Card className="info-panel" bordered={false}>
                    <Typography.Title level={5} className="section-title">
                        Faol karta
                    </Typography.Title>
                    <Typography.Paragraph className="section-text">
                        {activeCard ? maskCardPan(activeCard.pan) : 'Faol karta topilmadi'}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="section-text">
                        Amal qilish muddati: {activeCard?.expiry_date || '—'}
                    </Typography.Paragraph>
                    {subscriptionStatus?.is_active && (
                        <Button block style={{ marginTop: 8 }} onClick={() => navigate(buildBotPath(bot_id, '/card/change'))}>
                            Kartani almashtirish
                        </Button>
                    )}
                </Card>

                {pendingBlock && (
                    <Card className="info-panel" bordered={false}>
                        <Typography.Title level={5} className="section-title">
                            Obuna bekor qilingan
                        </Typography.Title>
                        <Typography.Paragraph className="section-text">
                            Kanalga kirish {formatDateTime(pendingBlock.blocked_from)} gacha saqlanadi.
                        </Typography.Paragraph>
                        <Typography.Paragraph className="section-text">
                            Shundan so'ng {formatDateTime(pendingBlock.blocked_until)} gacha qayta obuna bo'la olmaysiz.
                        </Typography.Paragraph>
                    </Card>
                )}

                <Card className="info-panel" bordered={false}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Button block onClick={() => navigate(buildBotPath(bot_id, '/onboarding/history'))}>
                            To'lovlar tarixini ko'rish
                        </Button>
                        {pendingBlock ? (
                            <Button
                                block
                                href={SUPPORT_LINK}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(event) => openTelegramLink(webApp, SUPPORT_LINK, event)}
                            >
                                Qo'llab-quvvatlash
                            </Button>
                        ) : (
                            <Button block danger onClick={handleUnsubscribe} loading={isUnsubscribing}>
                                Obunani bekor qilish
                            </Button>
                        )}
                    </Space>
                    {!pendingBlock && unsubscribeResult?.access_until && (
                        <Typography.Paragraph className="section-text" style={{ marginTop: 12 }}>
                            Bekor qilingandan so'ng kirish {formatDateTime(unsubscribeResult.access_until)} gacha saqlanadi.
                        </Typography.Paragraph>
                    )}
                </Card>
            </Flex>
        </>
    );
};

export default ClassicSubscriptionPanel;
